import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { ENVIRONMENT } from '../../../environments/environment';
import { LealOauthTokenService } from './leal-oauth-token.service';

const OTP_PATH_MARKERS = ['/v1/otp/otp/generate', '/v1/otp/otp/validate'] as const;

/** Prevents infinite 401 → refresh → 401 loops. */
const RETRY_HEADER = 'X-Leal-Otp-Auth-Retry';

function isLealOtpUrl(url: string): boolean {
  return OTP_PATH_MARKERS.some((p) => url.includes(p));
}

/**
 * Mirrors legacy {@link AuthInterceptor}: OTP calls get `Authorization: Bearer <token>`;
 * on 401, obtains client_credentials token, stores it, retries once.
 */
export const lealOtpAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const oauth = inject(LealOauthTokenService);
  if (!isLealOtpUrl(req.url)) {
    return next(req);
  }
  const token = oauth.getStoredAccessToken();
  const withBearer =
    token && !req.headers.has(RETRY_HEADER)
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(withBearer).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (req.headers.has(RETRY_HEADER)) {
        return throwError(() => err);
      }
      if (!ENVIRONMENT.lealOAuth?.authUrl) {
        return throwError(() => err);
      }
      return oauth.fetchClientCredentialsToken().pipe(
        switchMap((res) => {
          oauth.saveAccessToken(res.access_token);
          const retry = req.clone({
            setHeaders: {
              Authorization: `Bearer ${res.access_token}`,
              [RETRY_HEADER]: '1',
            },
          });
          return next(retry);
        }),
        catchError((refreshErr) => throwError(() => refreshErr)),
      );
    }),
  );
};
